import { reduce } from '@atlaskit/adf-utils/reduce';
import type { ADFEntity } from '@atlaskit/adf-utils/types';

import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';
import { fg } from '@confluence/feature-gating';
import { getSessionData } from '@confluence/session-data/entry-points/SessionData.ts';

import {
	hasMergedCellSpan,
	SUPPORTED_MARK_TYPES,
	SUPPORTED_NODE_TYPES,
} from './markdownSupportedSchema.ts';
import { ValidateConvertPageToMarkdownModeQuery } from './ValidateConvertPageToMarkdownModeQuery.agggraphql.ts';
import type {
	ValidateConvertPageToMarkdownModeQuery$data,
	ValidateConvertPageToMarkdownModeQuery$variables,
} from './__generated__/ValidateConvertPageToMarkdownModeQuery.graphql.ts';

/**
 * Returns `true` when the supplied document contains nodes / marks that don't
 * round-trip cleanly into the markdown pipeline (panels, expands, layouts,
 * status, date, mention, smart cards, media, custom marks, tables with merged
 * cells, etc.). Callers use this to decide whether to surface a confirmation
 * dialog before kicking off a rich-text → markdown conversion.
 *
 * The decision is based purely on **node `type`** and **mark `type`** — the
 * content of supported nodes is irrelevant.
 */

const isNodeLossy = (node: ADFEntity): boolean => {
	if (!SUPPORTED_NODE_TYPES.has(node.type)) {
		return true;
	}

	if (Array.isArray(node.marks)) {
		for (const mark of node.marks) {
			if (mark && !SUPPORTED_MARK_TYPES.has(mark.type)) {
				return true;
			}
		}
	}

	if ((node.type === 'tableCell' || node.type === 'tableHeader') && hasMergedCellSpan(node)) {
		return true;
	}

	return false;
};

/**
 * Inspect the supplied ADF document and return `true` if any node or mark
 * inside it is not safely round-trippable into markdown.
 *
 * `undefined` / `null` / non-ADF input → `false` (nothing to lose).
 */
export const isMarkdownConversionLossyLocal = (adf: unknown): boolean => {
	if (adf === undefined || adf === null) {
		return false;
	}
	if (typeof adf !== 'object' || typeof (adf as { type?: unknown }).type !== 'string') {
		return false;
	}

	return reduce<boolean>(adf as ADFEntity, (acc, node) => acc || isNodeLossy(node), false);
};

const isMarkdownConversionLossyGraphql = async (
	contentId: string,
): Promise<boolean | undefined> => {
	try {
		const { cloudId } = await getSessionData();
		if (!cloudId) {
			return undefined;
		}

		const { data } = await queryInterop<
			ValidateConvertPageToMarkdownModeQuery$data,
			ValidateConvertPageToMarkdownModeQuery$variables
		>({
			query: ValidateConvertPageToMarkdownModeQuery,
			variables: { cloudId, contentId },
			fetchPolicy: 'network-only',
			interopApolloAggClient: true,
		});

		const isLossless = data?.confluence_validateConvertPageToMarkdownMode?.isLossless;
		return typeof isLossless === 'boolean' ? !isLossless : undefined;
	} catch {
		return undefined;
	}
};

/**
 * Prefer the BE markdown conversion validator when gated on; fall back to the
 * local ADF walk for control, missing session data, GraphQL errors, or
 * incomplete responses.
 */
export const isMarkdownConversionLossy = async (
	adf: unknown,
	contentId?: string,
): Promise<boolean> => {
	if (contentId && fg('cc-markdown-mode-lossy-validation-query')) {
		const graphqlResult = await isMarkdownConversionLossyGraphql(contentId);
		if (typeof graphqlResult === 'boolean') {
			return graphqlResult;
		}
	}

	return isMarkdownConversionLossyLocal(adf);
};
